/**
 * Edit Card Info: the card's validity and the stand-in card, and nothing else.
 *
 * Ported from `legacy/frontendx/src/apps/mrm/components/MrmRowDetailView/
 * MrmCardInfoEditModal.tsx` and the handler beside it, field for field — two
 * across, the name and the number shown but not editable, the date and No
 * Expiry on one line, the stand-in card under them.
 *
 * Why the first two are there at all, when neither can be changed: the card
 * being edited has to be named, and a dialog headed "Edit Card Info" over a
 * page of five other cards does not name it. Legacy showed them for the same
 * reason. The name is the member's, and the number is reissued by an amendment
 * — typing over either is not an edit, it is a different member.
 *
 * The generic `EditDialog` drew this from field metadata before: three stacked
 * controls, no card identified, and No Expiry a row of its own under a date it
 * governs. The rule between those two is the whole form, so this one is written
 * out.
 */

import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Modal } from "@/components/ui/dialog";
import { Checkbox, Field, Form, Input } from "@/components/ui/field";
import type { TranslationKey } from "@/constants/i18n";
import { type Member, cardNameOf } from "@/pages/MembersPage";
import { api, errorMessage, ifMatch } from "@/services/api";
import { atLeast, useRank } from "@/utils/permissions";
import { sanitizeTemporaryCard, temporaryCardError } from "@/utils/temporary-card";
import { useTranslation } from "@/store/locale";

export function CardInfoDialog({
  member,
  onClose,
  onSaved,
}: {
  member: Member;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslation();
  // The date survives the flag being ticked, so unticking it does not lose the
  // day that was on the card — legacy kept it in state the same way.
  const [validUntil, setValidUntil] = useState(member.valid_until ?? "");
  const [noExpiry, setNoExpiry] = useState(member.no_expiry);
  const [temporary, setTemporary] = useState(() =>
    sanitizeTemporaryCard(member.temporary_card_number ?? ""),
  );
  const [badTemporary, setBadTemporary] = useState<TranslationKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Whether how long the card runs is this person's to change.
   *
   * A Supervisor and an Associate open this sheet for one thing: the stand-in
   * card handed over the counter while a card is reprinted. How long the card
   * itself is good for is a Manager's — legacy drew the same line, in
   * `hasRestrictedCardInfoEdit`, and disabled the date and No Expiry together
   * rather than hiding them: what the card says is worth reading even where it
   * is not yours to set.
   */
  const validity = atLeast(useRank(), "manager");

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/members/${member.id}/card-info`, body, ifMatch(member)),
    onSuccess: () => {
      toast.success(t("md.cardInfoUpdated"));
      onSaved();
    },
    onError: (err) => setError(errorMessage(err, t("md.cardInfoFailed"))),
  });

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    setError(null);
    const bad = temporaryCardError(temporary);
    if (bad) return setBadTemporary(bad);
    save.mutate({
      // The flag and the date are one answer, so both go every time: ticking
      // No Expiry clears the day, and unticking it has to put one back, which
      // a payload carrying only the date it happens to hold would not do. On
      // the two counter ranks neither goes at all — the fields were never
      // theirs to touch, and sending back what was already on the card would
      // write the member's own validity on every stand-in card typed.
      ...(validity
        ? noExpiry
          ? { no_expiry: true, valid_until: null }
          : { no_expiry: false, valid_until: validUntil }
        : {}),
      // Sent even when empty: that is how the stand-in card is handed back.
      temporary_card_number: temporary || null,
    });
  };

  // Unique, and pressed directly. A footer button reaches its form across the
  // sheet by `form="…"`, which the browser resolves by searching the whole
  // document for that id — so a literal shared with another dialog submits
  // whichever rendered first, sending that form's untouched values however much
  // was typed into this one. `EditDialog` learned the same lesson; here the
  // button also calls the handler itself, so nothing depends on the lookup.
  const formId = useId();
  return (
    <Modal
      open
      onClose={onClose}
      title={t("md.editCardInfo")}
      // Legacy's 560px: two fields across, and neither of them cramped.
      className="sm:max-w-[35rem]"
      footer={
        <>
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => submit()}
            className="flex-1"
            loading={save.isPending}
            loadingLabel={t("common.saving")}
            // A card is valid until a day or it never lapses; neither answer
            // is no answer, and legacy held Submit shut on exactly this —
            // except where the two are not being asked, since a sheet whose
            // only field is the stand-in card cannot answer them.
            disabled={validity && !noExpiry && !validUntil}
          >
            {t("common.submit")}
          </Button>
        </>
      }
    >
      <Form id={formId} className="grid gap-4 pb-2 sm:grid-cols-2" onSubmit={submit}>
        <Field label={t("mem.cardName")} htmlFor="card-info-name">
          <Input id="card-info-name" value={cardNameOf(member)} disabled readOnly />
        </Field>
        <Field label={t("mem.cardNumber")} htmlFor="card-info-number">
          <Input
            id="card-info-number"
            value={member.card_number ?? t("md.na")}
            disabled
            readOnly
          />
        </Field>
        <Field label={t("md.validUntil")} htmlFor="card-info-valid-until">
          <DatePicker
            id="card-info-valid-until"
            value={validUntil}
            onChange={setValidUntil}
            disabled={noExpiry || !validity}
            placeholder={t("mem.selectDate")}
          />
        </Field>
        {/* Beside the date rather than under it: it is the answer to the same
            question, and the label above it would read as a second field. The
            height is the date field's, so the two sit on one line. */}
        <div className="flex h-10 items-center self-end">
          <Checkbox
            checked={noExpiry}
            onChange={(event) => setNoExpiry(event.target.checked)}
            disabled={!validity}
            label={t("mem.noExpiry")}
          />
        </div>
        <Field
          label={t("mem.temporaryCardNumber")}
          htmlFor="card-info-temporary"
          error={badTemporary ? t(badTemporary) : undefined}
        >
          <Input
            id="card-info-temporary"
            inputMode="numeric"
            autoComplete="off"
            placeholder={t("new.enterTemporaryCard")}
            value={temporary}
            aria-invalid={badTemporary ? true : undefined}
            onChange={(event) => {
              const next = sanitizeTemporaryCard(event.target.value);
              setTemporary(next);
              // Once it has been refused, it is judged on every keystroke —
              // the field says it is right again as soon as it is, rather than
              // holding the complaint until Submit.
              if (badTemporary) setBadTemporary(temporaryCardError(next) ?? null);
            }}
            onBlur={() => setBadTemporary(temporaryCardError(temporary) ?? null)}
          />
        </Field>
        {error ? (
          <p role="alert" className="text-danger text-callout sm:col-span-2">
            {error}
          </p>
        ) : null}
      </Form>
    </Modal>
  );
}
